import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
  Input,
} from "antd";
import {
  UploadOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  MessageOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment-timezone";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;
import { ApiRequest } from "../../../services/api/api";
import { getUserId } from "../../../services/lib/utils/auth.utils";
import { calculateStatus, Status, getStatusColor } from "../task-status-utils";
import {
  normalizeTaskCommentDisplayType,
  taskCommentTypeForApi,
  TaskCommentDisplayType,
  TaskCommentInputType,
} from "../task-comment-utils";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 如果你的列表页不是 /tasks，改这里
const LIST_PATH = "/tasks";

const normalizeStatus = (value: any): Status => {
  const text = String(value ?? "").trim().toLowerCase();
  switch (text) {
    case "in_progress":
    case "in progress":
    case "in-progress":
      return "In Progress";
    case "completed":
    case "complete":
    case "done":
      return "Completed";
    case "overdue":
    case "blocked":
    case "late":
      return "Overdue";
    case "not started":
    case "not_started":
    case "not-started":
    default:
      return "Not Started";
  }
};

type CheckpointStatus = "processing" | "overdue" | "completed";

type Checkpoint = {
  checkpointId?: number;
  name: string;
  dueDate?: string | null;
  status?: CheckpointStatus;
  managerId?: number;
  managerName?: string;
};

type Task = {
  id: string;
  title: string;
  description?: string;
  manager?: string;
  managerId?: number;
  assignees?: string[];
  assigneeExecutionDetails?: { name: string; details: string }[];
  followers?: string[];
  startDate?: string | null;
  dueDate?: string | null;
  checkpoints?: Checkpoint[];
  status: Status;
  confirmedUserIds?: number[];
};

const CONFIRMED_MEMBER_STYLE: React.CSSProperties = {
  color: "#52c41a",
  fontWeight: 600,
};

const resolveParticipantUserId = (
  name: string,
  role: string,
  participants: Participant[],
  fallbackUserId?: number
): number | undefined => {
  if (fallbackUserId && fallbackUserId > 0) {
    return fallbackUserId;
  }
  const participant = participants.find(
    (p) => p.role === role && p.name.trim() === name.trim()
  );
  return participant?.userId;
};

const renderConfirmedMemberName = (
  name: string,
  role: string,
  participants: Participant[],
  confirmedUserIds: number[],
  fallbackUserId?: number
): React.ReactNode => {
  const userId = resolveParticipantUserId(name, role, participants, fallbackUserId);
  const isConfirmed = !!userId && confirmedUserIds.includes(userId);
  if (isConfirmed) {
    return <span style={CONFIRMED_MEMBER_STYLE}>{name}</span>;
  }
  return name;
};

const renderConfirmedMemberList = (
  names: string[],
  role: string,
  participants: Participant[],
  confirmedUserIds: number[]
): React.ReactNode => {
  if (!names.length) return "-";
  return names.map((name, idx) => (
    <React.Fragment key={`${role}-${name}-${idx}`}>
      {idx > 0 && ", "}
      {renderConfirmedMemberName(name, role, participants, confirmedUserIds)}
    </React.Fragment>
  ));
};

const buildAssigneeDetailRows = (
  task: Task,
  participants: Participant[]
): { name: string; details: string }[] => {
  const detailsByName = new Map<string, string>();
  (task.assigneeExecutionDetails ?? []).forEach((row) => {
    const name = row.name?.trim();
    if (name) {
      detailsByName.set(name, row.details?.trim() || "-");
    }
  });

  const assigneeNames =
    (task.assignees ?? []).map((n) => n.trim()).filter(Boolean).length > 0
      ? (task.assignees ?? []).map((n) => n.trim()).filter(Boolean)
      : participants
          .filter((p) => p.role === "assignee" && p.name.trim())
          .map((p) => p.name.trim());

  if (!assigneeNames.length) {
    return [{ name: "-", details: "-" }];
  }

  return assigneeNames.map((name) => {
    const participant = participants.find(
      (p) => p.role === "assignee" && p.name.trim() === name
    );
    return {
      name,
      details:
        detailsByName.get(name) ||
        participant?.executionDetails?.trim() ||
        "-",
    };
  });
};

const TASK_INFO_BORDER = "1px solid #d9d9d9";

const taskInfoGridStyles: Record<string, React.CSSProperties> = {
  root: {
    border: TASK_INFO_BORDER,
    background: "#fff",
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: TASK_INFO_BORDER,
  },
  metaRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    borderBottom: TASK_INFO_BORDER,
  },
  metaCell: {
    padding: "10px 16px",
    borderRight: TASK_INFO_BORDER,
  },
  metaCellLast: {
    padding: "10px 16px",
  },
  splitRow: {
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    borderBottom: TASK_INFO_BORDER,
  },
  splitHeader: {
    padding: "8px 16px",
    fontWeight: 600,
    borderBottom: TASK_INFO_BORDER,
    background: "#fafafa",
  },
  splitLeftCol: {
    borderRight: TASK_INFO_BORDER,
  },
  splitCell: {
    padding: "10px 16px",
    borderBottom: TASK_INFO_BORDER,
    minHeight: 40,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  labelValueRow: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    borderBottom: TASK_INFO_BORDER,
    padding: "10px 16px",
    gap: 16,
    alignItems: "start",
  },
  descriptionSection: {
    padding: "12px 16px",
    minHeight: 120,
  },
};

type CommentType = TaskCommentInputType;
type Comment = {
  id: string;
  type: TaskCommentDisplayType;
  content: string;
  creator: string;
  createdAt: string;
  reply?: string;
  attachments?: { name: string; url: string }[];
  /** From API: comment has a TaskMessage whose title contains "Notice" (home-style broadcast). */
  hasNoticeMessage?: boolean;
};

type Participant = {
  userId: number;
  name: string;
  role: string;
  executionDetails?: string;
};

const normalizeDate = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    // Backend sometimes returns naive datetime (no timezone info).
    // Treat it as UTC to avoid browser-local timezone drift.
    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(trimmed);
    const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
    const dateFromString = new Date(normalized);
    return Number.isNaN(dateFromString.getTime()) ? trimmed : dateFromString.toISOString();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toISOString();
};

const toNameList = (items: any): string[] =>
  (items || []).map((item: any) => {
    if (typeof item === "string") return item;
    if (item?.name) return item.name;
    if (item?.Name) return item.Name;
    if (item?.user) return item.user;
    if (item?.User) return item.User;
    return String(item ?? "");
  });

const getFileNameFromUrl = (url?: string): string => {
  if (!url) return "attachment";
  try {
    const normalizedUrl = String(url);
    const byPath = normalizedUrl.split("?")[0].split("#")[0];
    const name = byPath.substring(byPath.lastIndexOf("/") + 1);
    return name || "attachment";
  } catch {
    return "attachment";
  }
};

const buildSafeUploadName = (originalName: string): string => {
  const name = String(originalName || "").trim();
  const dotIndex = name.lastIndexOf(".");
  const ext = dotIndex > -1 ? name.slice(dotIndex) : "";
  const safeExt = /^[.][A-Za-z0-9]+$/.test(ext) ? ext.toLowerCase() : "";
  return `task_upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${safeExt}`;
};

const extractReplyTargetCommentId = (content?: string): string | null => {
  if (!content) return null;
  const match = String(content).match(/\[reply to #(\d+)\]/i);
  return match?.[1] ?? null;
};

const stripReplyPrefix = (content?: string): string => {
  if (!content) return "";
  return String(content).replace(/^\s*\[reply to #\d+\]\s*/i, "").trim();
};

const mapTaskComments = (rawComments: any[]): Comment[] =>
  (rawComments || []).map((c: any, index: number) => ({
    id: String(c?.commentId ?? c?.CommentId ?? index),
    type: normalizeTaskCommentDisplayType(
      c?.commentType ?? c?.CommentType ?? "Routine",
      c?.content ?? c?.Content ?? ""
    ),
    content: c?.content ?? c?.Content ?? "",
    creator: c?.createdByName ?? c?.CreatedByName ?? c?.createdBy ?? c?.CreatedBy ?? "",
    createdAt: normalizeDate(c?.createdAt ?? c?.CreatedAt) ?? new Date().toISOString(),
    reply: c?.reply ?? c?.Reply,
    attachments: (c?.attachments ?? c?.Attachments ?? c?.taskAttachment ?? c?.TaskAttachment ?? [])
      .map((att: any) => {
        const url = att?.fileUrl ?? att?.FileUrl ?? att?.url ?? att?.Url ?? "";
        return {
          name: att?.fileName ?? att?.FileName ?? getFileNameFromUrl(url),
          url: url || "#",
        };
      })
      .filter((att: any) => !!att?.url && att.url !== "#"),
    hasNoticeMessage: !!(c?.hasNoticeMessage ?? c?.HasNoticeMessage),
  }));

const mapTaskDetail = (data: any): {
  task: Task;
  comments: Comment[];
  participants: Participant[];
} => {
  const participants: Participant[] = (data?.participants ?? data?.Participants ?? [])
    .map((p: any) => {
      const idValue = parseInt(String(p?.userId ?? p?.UserId ?? 0), 10);
      return {
        userId: Number.isFinite(idValue) ? idValue : 0,
        name: (p?.name ?? p?.Name ?? "").trim(),
        role: (p?.role ?? p?.Role ?? "").toLowerCase(),
        executionDetails: (
          p?.executionDetails ??
          p?.ExecutionDetails ??
          p?.executeDetails ??
          p?.ExecuteDetails ??
          p?.details ??
          p?.Details ??
          ""
        ).trim(),
      };
    })
    .filter((p: Participant) => p.userId > 0);

  const managerIdRaw = data?.managerId ?? data?.ManagerId;
  const managerId = managerIdRaw ? parseInt(String(managerIdRaw), 10) : undefined;
  const managerName =
    data?.managerName ??
    data?.ManagerName ??
    data?.manager ??
    data?.Manager ??
    participants.find((p: Participant) => p.role === "manager")?.name ??
    "-";

  const confirmedUserIds: number[] = (data?.confirmedUserIds ?? data?.ConfirmedUserIds ?? [])
    .map((id: any) => parseInt(String(id), 10))
    .filter((id: number) => Number.isFinite(id) && id > 0);

  const startDate = normalizeDate(data?.startDate ?? data?.StartDate);
  const dueDate = normalizeDate(data?.dueDate ?? data?.DueDate);
  const originalStatus = normalizeStatus(data?.status ?? data?.Status ?? "Not Started");
  const checkpoints = (data?.checkpoints ?? data?.Checkpoints ?? []).map((c: any) => ({
    checkpointId: c?.checkpointId ?? c?.CheckpointId,
    name: c?.name ?? c?.Name,
    dueDate: c?.dueDate ?? c?.DueDate,
    isCompleted: c?.isCompleted ?? c?.IsCompleted ?? false,
    completedAt: c?.completedAt ?? c?.CompletedAt,
  }));
  
  // 根据 checkpoints 计算状态（新逻辑）
  const computedStatus = calculateStatus(originalStatus, startDate, dueDate, checkpoints);

  // 先获取所有评论，用于判断 checkpoint 是否完成
  const rawComments = (data?.comments ?? data?.Comments ?? []);
  const comments: Comment[] = mapTaskComments(rawComments);

  // 计算 checkpoint 状态
  const calculateCheckpointStatus = (checkpoint: any): CheckpointStatus => {
    const checkpointName = checkpoint?.name ?? checkpoint?.Name ?? "";
    const checkpointDueDate = normalizeDate(checkpoint?.dueDate ?? checkpoint?.DueDate);
    
    // 检查是否有完成该 checkpoint 的评论
    const isCompleted = comments.some((comment) => {
      const content = (comment.content || "").toLowerCase();
      const checkpointNameLower = checkpointName.toLowerCase();
      return (
        content.includes("checkpoint completed") &&
        content.includes(checkpointNameLower)
      );
    });

    if (isCompleted) {
      return "completed";
    }

    // 如果没有完成，检查是否 overdue
    if (checkpointDueDate) {
      const now = moment().tz(NZ_TIMEZONE);
      const due = toNZTime(checkpointDueDate);
      if (due && due.isBefore(now, "day")) {
        return "overdue";
      }
    }

    // 默认返回 processing（进行中）
    return "processing";
  };

  const task: Task = {
    id: String(data?.taskId ?? data?.TaskId ?? data?.id ?? data?.Id ?? ""),
    title: data?.title ?? data?.Title ?? "",
    description: data?.description ?? data?.Description ?? "",
    manager: managerName,
    managerId: Number.isFinite(managerId) && (managerId ?? 0) > 0 ? managerId : undefined,
    assignees: toNameList(data?.assignees ?? data?.Assignees),
    assigneeExecutionDetails: (() => {
      const fromAssignees = (data?.assignees ?? data?.Assignees ?? [])
        .filter((a: any) => typeof a === "object" && a != null)
        .map((a: any) => ({
          name: String(a?.name ?? a?.Name ?? a?.user ?? a?.User ?? "").trim(),
          details: String(
            a?.details ??
            a?.Details ??
            a?.executionDetails ??
            a?.ExecutionDetails ??
            a?.executeDetails ??
            a?.ExecuteDetails ??
            ""
          ).trim(),
        }))
        .filter((x: any) => !!x.name);

      const fromParticipants = participants
        .filter((p) => p.role === "assignee" && !!p.name)
        .map((p) => ({
          name: p.name || `User ${p.userId}`,
          details: (p.executionDetails || "").trim(),
        }));

      const merged = [...fromAssignees, ...fromParticipants];
      const seen = new Set<string>();
      return merged.filter((item) => {
        const key = `${item.name}::${item.details}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })(),
    followers: toNameList(data?.followers ?? data?.Followers),
    startDate: startDate,
    dueDate: dueDate,
    checkpoints: (data?.checkpoints ?? data?.Checkpoints ?? []).map((c: any) => ({
      checkpointId: c?.checkpointId ?? c?.CheckpointId ?? 0,
      name: c?.name ?? c?.Name ?? "",
      dueDate: normalizeDate(c?.dueDate ?? c?.DueDate),
      status: calculateCheckpointStatus(c),
      managerId: (() => {
        const raw = c?.managerId ?? c?.ManagerId;
        const id = raw != null ? parseInt(String(raw), 10) : 0;
        return Number.isFinite(id) && id > 0 ? id : undefined;
      })(),
      managerName: String(c?.managerName ?? c?.ManagerName ?? "").trim() || undefined,
    })),
    status: computedStatus,
    confirmedUserIds,
  };

  return { task, comments, participants };
};

const fetchTaskDetail = async (id: string) => {
  const taskId = parseInt(id, 10);
  if (!taskId) {
    throw new Error("Invalid task id.");
  }
  const result = await ApiRequest({
    otherUrl: "TaskManagement/GetTaskById",
    type: "get",
    params: { taskId },
  });
  const data = result?.data?.data ?? result?.data;
  if (!data) {
    throw new Error("Task not found.");
  }
  return mapTaskDetail(data);
};
/* ----------------------------------------------------------------- */

const statusTag = (s: Status) => <Tag color={getStatusColor(s)}>{s}</Tag>;

const TaskDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const [task, setTask] = useState<Task | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);

  // add comment modal
  const [commentVisible, setCommentVisible] = useState(false);
  const [commentType, setCommentType] = useState<CommentType>("Routine");
  const [commentContent, setCommentContent] = useState("");
  const [attachments, setAttachments] = useState<
    { name: string; url: string }[]
  >([]);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);

  // reply modal
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyToComment, setReplyToComment] = useState<Comment | null>(null);
  const [replyContent, setReplyContent] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const { task: mappedTask, comments: mappedComments, participants: mappedParticipants } =
        await fetchTaskDetail(id);
      let finalComments = [...mappedComments];

      const taskIdNum = parseInt(id, 10);
      if (taskIdNum) {
        try {
          const commentsRes = await ApiRequest({
            otherUrl: "TaskComment/GetCommentsByTaskId",
            type: "get",
            params: { taskId: taskIdNum },
            isShowSpinner: false,
            isNotShowSpinnerOverride: true,
          });
          const commentsData = commentsRes?.data?.data ?? commentsRes?.data;
          const rows = Array.isArray(commentsData)
            ? commentsData
            : (commentsData?.data ?? []);
          if (Array.isArray(rows) && rows.length > 0) {
            finalComments = mapTaskComments(rows);
          }
        } catch (commentError) {
          // Keep task-detail comments as fallback when comments endpoint is unavailable.
          console.warn("Failed to load comments from TaskComment API", commentError);
        }
      }

      finalComments.sort(
        (a, b) => (toNZTime(b.createdAt)?.valueOf() || 0) - (toNZTime(a.createdAt)?.valueOf() || 0)
      );
      setTask(mappedTask);
      setComments(finalComments);
      setParticipants(mappedParticipants);
      document.title = "Task Details";
    } catch (e: any) {
      message.error(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  const repliesByCommentId = useMemo(() => {
    const grouped: Record<string, Comment[]> = {};
    comments.forEach((comment) => {
      const targetId = extractReplyTargetCommentId(comment.content);
      if (!targetId) return;
      if (!grouped[targetId]) grouped[targetId] = [];
      grouped[targetId].push(comment);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) => (toNZTime(a.createdAt)?.valueOf() || 0) - (toNZTime(b.createdAt)?.valueOf() || 0)
      );
    });
    return grouped;
  }, [comments]);

  const tableComments = useMemo(
    () => comments.filter((comment) => !extractReplyTargetCommentId(comment.content)),
    [comments]
  );

  const uploadFile = useCallback(async (file: File): Promise<{ fileUrl: string; fileName: string; fileType: string } | null> => {
    try {
      const formData = new FormData();
      formData.append("imageFile", file, buildSafeUploadName(file.name));

      const response = await ApiRequest({
        otherUrl: "Common/UploadImage",
        method: "post",
        data: formData,
        isShowSpinner: false,
      });

      const fileUrl = response?.data?.data ?? response?.data ?? response;
      if (typeof fileUrl === "string" && fileUrl) {
        return {
          fileUrl,
          fileName: file.name,
          fileType: file.type || "unknown",
        };
      }
      return null;
    } catch (error) {
      console.error("Failed to upload file:", file.name, error);
      return null;
    }
  }, []);

  const handleAttachToNotice = useCallback(
    async (record: Comment, file: File) => {
      const taskIdNumber = parseInt(id || "", 10);
      const commentId = parseInt(record.id, 10);
      const uidRaw = getUserId();
      const uploadedBy = uidRaw ? parseInt(String(uidRaw), 10) : 0;
      if (!taskIdNumber || !commentId || !uploadedBy) {
        message.error("Invalid task, comment, or user.");
        return;
      }
      const key = `attach-${record.id}`;
      message.loading({ content: "Uploading...", key });
      const uploaded = await uploadFile(file);
      message.destroy(key);
      if (!uploaded) {
        message.error("Upload failed.");
        return;
      }
      try {
        await ApiRequest({
          otherUrl: "TaskComment/AddAttachmentsToComment",
          method: "post",
          data: {
            taskId: taskIdNumber,
            commentId,
            uploadedBy,
            attachments: [
              {
                fileUrl: uploaded.fileUrl,
                fileName: uploaded.fileName,
                fileType: uploaded.fileType,
              },
            ],
          },
          isShowSpinner: true,
        });
        setComments((prev) =>
          prev.map((c) =>
            c.id !== record.id
              ? c
              : {
                  ...c,
                  attachments: [...(c.attachments || []), { name: uploaded.fileName, url: uploaded.fileUrl }],
                }
          )
        );
        message.success("Attachment added");
      } catch (e: any) {
        message.error(e?.message || "Failed to save attachment");
      }
    },
    [id, uploadFile]
  );

  const handleDeleteAttachment = async (commentId: string, fileUrl: string) => {
    const numericCommentId = parseInt(String(commentId), 10);
    if (!numericCommentId || !fileUrl) {
      message.error("Attachment info is invalid.");
      return;
    }

    try {
      await ApiRequest({
        otherUrl: "TaskComment/DeleteAttachment",
        method: "post",
        data: {
          commentId: numericCommentId,
          fileUrl,
        },
        isShowSpinner: false,
      });

      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id !== String(commentId)) return comment;
          return {
            ...comment,
            attachments: (comment.attachments || []).filter((att) => att.url !== fileUrl),
          };
        })
      );
      message.success("Attachment deleted");
    } catch (error: any) {
      message.error(error?.message || "Failed to delete attachment");
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "type",
        width: 160,
        render: (v: Comment["type"]) => {
          const color =
            v === "Routine"
              ? "default"
              : v === "Reschedule"
              ? "warning"
              : v === "Apply Reschedule"
              ? "warning"
              : v === "Apply Completion"
              ? "gold"
              : v === "Complete"
              ? "success"
              : v === "Notice"
              ? "processing"
              : v === "Reply"
              ? "cyan"
              : "processing";
          return <Tag color={color}>{v}</Tag>;
        },
      },
      {
        title: "Content",
        dataIndex: "content",
        width: 360,
        render: (v: string) => (
          <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {v}
          </Paragraph>
        ),
      },
      { title: "Creator", dataIndex: "creator", width: 140 },
      {
        title: "Created at",
        dataIndex: "createdAt",
        width: 180,
        render: (d: string) => toNZTime(d)?.format("h:mm a DD/MM/YYYY") || "-",
      },
      {
        title: "Replies",
        dataIndex: "id",
        width: 280,
        render: (_: any, record: Comment) => {
          const replies = repliesByCommentId[record.id] || [];
          if (!replies.length) return "-";
          return (
            <div style={{ fontSize: 12, lineHeight: 1.6 }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                {replies.length} repl{replies.length > 1 ? "ies" : "y"}
              </Text>
              {replies.map((reply) => {
                const summary = stripReplyPrefix(reply.content);
                const at = toNZTime(reply.createdAt)?.format("h:mm a DD/MM/YYYY") || "";
                return (
                  <div key={reply.id} style={{ marginBottom: 6, whiteSpace: "pre-wrap" }}>
                    <Text>
                      [{reply.type}] {reply.creator}
                      {at ? ` · ${at}` : ""}: {summary || "(empty)"}
                    </Text>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        width: 100,
        render: (_: any, record: Comment) => (
          <Button
            type="link"
            size="small"
            icon={<MessageOutlined />}
            onClick={() => openReplyModal(record)}
          >
            Reply
          </Button>
        ),
      },
      {
        title: "Attachment",
        dataIndex: "attachments",
        width: 200,
        render: (files: Comment["attachments"] | undefined, record: Comment) => (
          <Space direction="vertical" size={6} align="start">
            {files && files.length > 0 ? (
              files.map((f) => (
                <Space key={`${record.id}-${f.url}`} size={8}>
                  <a href={f.url} target="_blank" rel="noreferrer">
                    {f.name}
                  </a>
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteAttachment(record.id, f.url)}
                    style={{ padding: 0, height: "auto" }}
                  >
                    Delete
                  </Button>
                </Space>
              ))
            ) : (
              <Text type="secondary">-</Text>
            )}
            {record.type === "Notice" || record.hasNoticeMessage ? (
              <Upload
                showUploadList={false}
                beforeUpload={async (file) => {
                  await handleAttachToNotice(record, file as File);
                  return false;
                }}
              >
                <Button size="small" type="default" icon={<UploadOutlined />}>
                  Attach
                </Button>
              </Upload>
            ) : null}
          </Space>
        ),
      },
    ],
    [repliesByCommentId, handleDeleteAttachment, handleAttachToNotice]
  );

  const openAddComment = (preset?: CommentType) => {
    setCommentType(preset || "Routine");
    setCommentContent("");
    setAttachments([]);
    setUploadFileList([]);
    setCommentVisible(true);
  };

  const openReplyModal = (comment: Comment) => {
    setReplyToComment(comment);
    setReplyContent("");
    setReplyVisible(true);
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      return message.warning("Please input reply content");
    }
    if (!replyToComment) {
      return message.error("No comment selected for reply");
    }

    const taskIdNumber = parseInt(id, 10);
    if (!taskIdNumber) {
      return message.error("Task id is invalid.");
    }
    const creatorRaw = getUserId();
    const createdBy = creatorRaw ? parseInt(String(creatorRaw), 10) : 0;
    if (!createdBy) {
      return message.error("Current user is not identified.");
    }

    // 构建回复内容，包含原评论的引用
    const replyContentWithRef = `[Reply to #${replyToComment.id}] ${replyContent.trim()}`;

    try {
      await ApiRequest({
        otherUrl: "TaskComment/AddComment",
        method: "post",
        data: {
          taskId: taskIdNumber,
          createdBy,
          // Use routine to stay compatible with older backend enum definitions.
          commentType: "Routine",
          content: replyContentWithRef,
          // 被回复人由后端根据 [Reply to #commentId] 自动加入通知
          notifyUserIds: [],
        },
        isShowSpinner: true,
      });

      message.success("Reply added successfully");
      setReplyVisible(false);
      setReplyToComment(null);
      setReplyContent("");
      await load();
    } catch (error: any) {
      message.error(error?.message || "Failed to add reply");
    }
  };

  const handleAddComment = async () => {
    if (!commentContent.trim()) {
      return message.warning("Please input content");
    }
    const taskIdNumber = parseInt(id, 10);
    if (!taskIdNumber) {
      return message.error("Task id is invalid.");
    }
    const creatorRaw = getUserId();
    const createdBy = creatorRaw ? parseInt(String(creatorRaw), 10) : 0;
    if (!createdBy) {
      return message.error("Current user is not identified.");
    }

    const notifyUserIds = participants
      .map((p) => p.userId)
      .filter((userId) => Number.isFinite(userId) && userId > 0);

    // 上传附件
    const uploadedAttachments: { fileUrl: string; fileName: string; fileType: string }[] = [];
    let uploadFailedCount = 0;
    if (uploadFileList.length > 0) {
      message.loading({ content: "Uploading attachments...", key: "upload" });
      for (const fileItem of uploadFileList) {
        if (fileItem.originFileObj) {
          const uploaded = await uploadFile(fileItem.originFileObj);
          if (uploaded) {
            uploadedAttachments.push(uploaded);
          } else {
            uploadFailedCount += 1;
          }
        }
      }
      message.destroy("upload");
    }

    if (uploadFileList.length > 0 && uploadedAttachments.length === 0) {
      return message.error("Attachment upload failed. Please retry with a smaller file or supported format.");
    }

    if (uploadFailedCount > 0) {
      message.warning(`${uploadFailedCount} attachment(s) failed to upload and were skipped.`);
    }

    await ApiRequest({
      otherUrl: "TaskComment/AddComment",
      method: "post",
      data: {
        taskId: taskIdNumber,
        createdBy,
        commentType: taskCommentTypeForApi(commentType),
        content: commentContent.trim(),
        notifyUserIds,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
      },
      isShowSpinner: true,
    });

    setCommentVisible(false);
    setCommentContent("");
    setAttachments([]);
    setUploadFileList([]);
    message.success("Comment added successfully");
    await load();
  };

  return (
    <div style={{ padding: 16, background: "#f7f9fc", minHeight: "100vh" }}>
      <Card
        loading={loading}
        bordered={false}
        style={{
          maxWidth: 1600,
          margin: "0 auto",
          boxShadow: "0 2px 10px rgba(0,0,0,.06)",
        }}
        bodyStyle={{ padding: 24 }}
      >
        {/* Header */}
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={2}>
              <Title level={3} style={{ margin: 0 }}>
                Task Details
              </Title>
              <Text type="secondary">
                Read-only task content with comments, reply and attachments
              </Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={load}>
                Refresh
              </Button>
              <Button onClick={() => history.push(LIST_PATH)}>Back to List</Button>
            </Space>
          </Col>
        </Row>

        {/* Read-only content */}
        <Card size="small" style={{ marginTop: 16 }} bodyStyle={{ padding: 0 }}>
          {task ? (
            <div style={taskInfoGridStyles.root}>
              <div style={taskInfoGridStyles.titleRow}>
                <Text strong style={{ fontSize: 16 }}>
                  {task.title || "-"}
                </Text>
                {statusTag(task.status)}
              </div>

              <div style={taskInfoGridStyles.metaRow}>
                <div style={taskInfoGridStyles.metaCell}>
                  <Text strong>Manager</Text>
                  <div style={{ marginTop: 4 }}>
                    {task.manager && task.manager !== "-"
                      ? renderConfirmedMemberName(
                          task.manager,
                          "manager",
                          participants,
                          task.confirmedUserIds || [],
                          task.managerId
                        )
                      : "-"}
                  </div>
                </div>
                <div style={taskInfoGridStyles.metaCell}>
                  <Text strong>Start</Text>
                  <div style={{ marginTop: 4 }}>
                    {task.startDate
                      ? toNZTime(task.startDate)?.format("YYYY/MM/DD")
                      : "-"}
                  </div>
                </div>
                <div style={taskInfoGridStyles.metaCellLast}>
                  <Text strong>Due</Text>
                  <div style={{ marginTop: 4 }}>
                    {task.dueDate
                      ? toNZTime(task.dueDate)?.format("YYYY/MM/DD")
                      : "-"}
                  </div>
                </div>
              </div>

              {(() => {
                const assigneeRows = buildAssigneeDetailRows(task, participants);
                return (
                  <div style={taskInfoGridStyles.splitRow}>
                    <div style={taskInfoGridStyles.splitLeftCol}>
                      <div style={taskInfoGridStyles.splitHeader}>Assignees</div>
                      {assigneeRows.map((row, idx) => (
                        <div
                          key={`assignee-${row.name}-${idx}`}
                          style={{
                            ...taskInfoGridStyles.splitCell,
                            borderBottom:
                              idx === assigneeRows.length - 1 ? "none" : TASK_INFO_BORDER,
                          }}
                        >
                          {row.name === "-"
                            ? "-"
                            : renderConfirmedMemberName(
                                row.name,
                                "assignee",
                                participants,
                                task.confirmedUserIds || []
                              )}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={taskInfoGridStyles.splitHeader}>Execution Details</div>
                      {assigneeRows.map((row, idx) => (
                        <div
                          key={`execution-${row.name}-${idx}`}
                          style={{
                            ...taskInfoGridStyles.splitCell,
                            borderBottom:
                              idx === assigneeRows.length - 1 ? "none" : TASK_INFO_BORDER,
                          }}
                        >
                          {row.details}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={taskInfoGridStyles.labelValueRow}>
                <Text strong>Followers</Text>
                <div>
                  {renderConfirmedMemberList(
                    task.followers || [],
                    "follower",
                    participants,
                    task.confirmedUserIds || []
                  )}
                </div>
              </div>

              <div style={taskInfoGridStyles.labelValueRow}>
                <Text strong>Check points</Text>
                <div>
                  {task.checkpoints && task.checkpoints.length ? (
                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                      {task.checkpoints.map((c, index) => {
                        const badgeStatus =
                          c.status === "completed"
                            ? "success"
                            : c.status === "overdue"
                            ? "error"
                            : "processing";
                        const statusText =
                          c.status === "completed"
                            ? "Completed"
                            : c.status === "overdue"
                            ? "Overdue"
                            : "In Progress";

                        const managerPart = c.managerName ? ` · Owner: ${c.managerName}` : "";

                        return (
                          <div key={c.checkpointId || c.name}>
                            <Badge
                              status={badgeStatus}
                              text={`${index + 1}. ${c.name} · ${statusText}${
                                c.dueDate
                                  ? ` · Due ${toNZTime(c.dueDate)?.format("YYYY/MM/DD")}`
                                  : ""
                              }${managerPart}`}
                            />
                          </div>
                        );
                      })}
                    </Space>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              <div style={taskInfoGridStyles.descriptionSection}>
                <Text strong>Task Description Detail</Text>
                <Paragraph style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>
                  {task.description || "-"}
                </Paragraph>
              </div>
            </div>
          ) : (
            <Empty description="No task" style={{ padding: 24 }} />
          )}
        </Card>

        {/* Comments */}
        <Card
          size="small"
          title="Comments"
          style={{ marginTop: 16 }}
          extra={
            <Space>
              <Button
                onClick={() => openAddComment("Routine")}
                icon={<SendOutlined />}
              >
                Progress Update
              </Button>
              <Button
                onClick={() => openAddComment("Apply Reschedule")}
                icon={<FieldTimeOutlined />}
              >
                Apply Reschedule
              </Button>
              <Button
                type="primary"
                onClick={() => openAddComment("Apply Completion")}
                icon={<CheckCircleOutlined />}
              >
                Apply Completion
              </Button>
            </Space>
          }
        >
          <Table<Comment>
            rowKey="id"
            columns={columns as any}
            dataSource={tableComments}
            pagination={{ pageSize: 8 }}
            scroll={{ x: 1420 }}
            locale={{ emptyText: <Empty description="No comments" /> }}
          />
        </Card>
      </Card>

      {/* Add Comment Modal（antd v4 用 visible） */}
      <Modal
        title={`Progress Update${task ? ` - Task ID: ${task.id}` : ""}`}
        visible={commentVisible}
        okText="Add Comment"
        cancelText="Cancel"
        onOk={handleAddComment}
        onCancel={() => setCommentVisible(false)}
        width={640}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>
            Type <span style={{ color: "red" }}>*</span>
          </div>
          <Select
            value={commentType}
            onChange={(v: CommentType) => setCommentType(v)}
            style={{ width: "100%" }}
          >
            <Option value="Routine">Routine</Option>
            <Option value="Apply Reschedule">Apply Reschedule</Option>
            <Option value="Apply Completion">Apply Completion</Option>
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>
            Content <span style={{ color: "red" }}>*</span>
          </div>
          <TextArea
            rows={5}
            placeholder="Enter progress update..."
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>Attachment</div>
          <Upload
            multiple
            fileList={uploadFileList}
            beforeUpload={() => false}
            onChange={({ fileList }) => {
              setUploadFileList(fileList);
              setAttachments(
                fileList.map((f: any) => ({
                  name: f.name,
                  url: f.thumbUrl || "#",
                }))
              );
            }}
            onRemove={(file) => {
              setUploadFileList((prev) => prev.filter((f) => f.uid !== file.uid));
              setAttachments((prev) => prev.filter((a) => a.name !== file.name));
              return true;
            }}
            accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
            Accepted: .jpg/.png/.pdf/.excel/.docs/word
            </Text>
          </div>
        </div>
        {task && (
          <div
            style={{
              background: "#fafafa",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              padding: 16,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Notification</div>
            <Text type="secondary">All Task Members</Text>
            <div>
              <Text>
                {task.title || `Task #${task.id}`} Progress Update: {commentType} comment is added.
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        title="Reply to Comment"
        visible={replyVisible}
        okText="Send Reply"
        onOk={handleReply}
        onCancel={() => {
          setReplyVisible(false);
          setReplyToComment(null);
          setReplyContent("");
        }}
        width={600}
      >
        {replyToComment && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Card size="small" style={{ backgroundColor: "#f5f5f5" }}>
              <Text type="secondary">Original Comment:</Text>
              <div style={{ marginTop: 8 }}>
                <Tag color="blue">{replyToComment.type}</Tag>
                <Text strong style={{ marginLeft: 8 }}>{replyToComment.creator}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {toNZTime(replyToComment.createdAt)?.format("h:mm a DD/MM/YYYY")}
                </Text>
              </div>
              <Paragraph style={{ margin: "8px 0 0", whiteSpace: "pre-wrap" }}>
                {replyToComment.content}
              </Paragraph>
            </Card>
            <TextArea
              rows={4}
              placeholder="Write your reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
            />
          </Space>
        )}
      </Modal>

    </div>
  );
};

export default TaskDetailPage;
